import {
  interfaceProjectAsyncAtomFamily,
  interfacePageAsyncAtomFamily,
  nodeAtomFamily,
  NodeType,
  AssetType,
  previewNodeIdAtom,
  isDefaultNode,
  assetAsyncAtomFamily,
  determineAssetType,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";
import { DefaultNodeData as NewDefaultNodeData } from "@zapier/canvas-state/src/types";

import { getAssetId } from "../../utils/getAssetId";
import getBaseUrl from "../../utils/getBaseUrl";
import { getTablesOrigin } from "../../utils/urls";

import { ModalContainer } from "./ModalContainer";

function SuspendedComponent({ previewNodeId }: { previewNodeId: string }) {
  const setPreviewNodeIdAtom = useSetAtom(previewNodeIdAtom);
  const node = useAtomValue(nodeAtomFamily(previewNodeId));
  const assetType = determineAssetType(
    (node.data as any).services,
    node.type as NodeType,
  );
  const selectedAssetId = getAssetId(node.data, assetType);
  const asset = useAtomValue(
    assetAsyncAtomFamily({
      assetType,
      assetId: selectedAssetId,
      interfaceProjectId: (node.data as NewDefaultNodeData).projectId,
    }),
  );
  const url = getUrl(asset, assetType, selectedAssetId);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPreviewNodeIdAtom(null);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [setPreviewNodeIdAtom]);

  const handleClose = () => {
    setPreviewNodeIdAtom(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (
    assetType === "interface" &&
    isDefaultNode(node) &&
    node.data.projectId &&
    node.data.pageId
  ) {
    return (
      <InterfaceDetailModal
        interfaceProjectId={node.data.projectId}
        interfacePageId={node.data.pageId}
        previewNodeId={previewNodeId}
      />
    );
  }

  return (
    <ModalContainer
      onOverlayClick={handleOverlayClick}
      title={`Asset Detail Modal: ${assetType} - ${selectedAssetId}`}
      url={url}
      assetType={assetType}
      // @ts-expect-error TODO check type of asset to make sure its not error type
      disableIframe={assetType === "zap" && !asset?.is_published}
    />
  );
}

export function AssetDetailModal({ previewNodeId }: { previewNodeId: string }) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent previewNodeId={previewNodeId} />
    </Suspense>
  );
}

function getUrl(asset: any, assetType: AssetType, id: string) {
  if (assetType === "zap") {
    if (asset.is_published) {
      return `${getBaseUrl()}/editor/${id}/published?is_view_mode=true`;
    } else {
      return `${getBaseUrl()}/editor/${id}/draft`;
    }
  }
  if (assetType === "table") {
    return `https://${getTablesOrigin()}/app/tables/t/${id}`;
  }
  return;
}

function InterfaceDetailModal({
  interfaceProjectId,
  interfacePageId,
  previewNodeId,
}: {
  interfaceProjectId: string;
  interfacePageId: string;
  previewNodeId: string;
}) {
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);
  const interfaceProject = useAtomValue(
    interfaceProjectAsyncAtomFamily({
      interfaceProjectId: interfaceProjectId,
      nodeId: previewNodeId,
    }),
  );
  const page = useAtomValue(
    interfacePageAsyncAtomFamily({
      interfaceProjectId: interfaceProjectId,
      interfacePageId: interfacePageId,
      nodeId: previewNodeId,
    }),
  );
  const projectSlug = interfaceProject?.slug;
  const pageSlug = page?.slug;
  const customDomain = interfaceProject?.customDomain;

  const url = customDomain
    ? `https://${customDomain}/${pageSlug}`
    : `https://${projectSlug}.zapier.app/${pageSlug}`;

  const handleClose = () => {
    setPreviewNodeId(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <ModalContainer
      onOverlayClick={handleOverlayClick}
      title={`Asset Detail Modal: ${interfaceProject?.name} - ${page?.name}`}
      url={url}
      assetType="interface"
      disableIframe={false}
    />
  );
}
